import { resolveBoxTiming } from "./resolveBoxTiming.es6";
import { audioCueManual } from "./audio_cue_manual.es6";

const FPS = 30;

const startSecOf = (sound) => Math.max(0, Number(sound.startTime) || 0);

const isCandidate = (tb) =>
  !!tb &&
  tb.pluginId === "AudioCue" &&
  Array.isArray(tb.state?.soundList) &&
  tb.state.soundList.length > 0;

// Places one list sound on the timeline. The list's clock started with the box,
// so a sound plays at the box start plus its startTime (seconds).
function soundTimeline(box, baseFrame, sound, nextStartSec) {
  const startSec = startSecOf(sound);
  let endSec = Number(sound.endTime);
  if (!(endSec > startSec)) endSec = startSec + (Number(sound.duration) || 0);
  // The list played one sound at a time: a later start cut the earlier one.
  const open = !(endSec > startSec);
  if (nextStartSec != null && (open || endSec > nextStartSec)) endSec = nextStartSec;
  const timeline = {
    ...(box.timeline || {}),
    startFrame: baseFrame + startSec * FPS,
    trimStart: 0,
  };
  delete timeline.splitGroup;
  if (endSec > startSec) {
    const len = (endSec - startSec) * FPS;
    timeline.endFrame = timeline.startFrame + len;
    timeline.trimEnd = len;
    timeline.userSetEndFrame = true;
  } else {
    delete timeline.endFrame;
    delete timeline.trimEnd;
    delete timeline.userSetEndFrame;
  }
  return timeline;
}

// The list that holds the box (scene, contained view or sortable container), or
// null when the box is not found in one.
function holderOf(out, box, boxId) {
  for (const key of ["navItemsById", "containedViewsById"]) {
    const view = out[key][box.parent];
    if (view && Array.isArray(view.boxes)) {
      if (view.boxes.indexOf(boxId) < 0) return null;
      return { list: view.boxes, set: (boxes) => (out[key][box.parent] = { ...view, boxes }) };
    }
  }
  const parent = out.boxesById[box.parent];
  const sc = parent?.sortableContainers?.[box.container];
  if (!sc || !Array.isArray(sc.children) || sc.children.indexOf(boxId) < 0) return null;
  return {
    list: sc.children,
    set: (children) =>
      (out.boxesById[box.parent] = {
        ...parent,
        sortableContainers: { ...parent.sortableContainers, [box.container]: { ...sc, children } },
      }),
  };
}

// Ids for the extra sounds that no box or toolbar uses yet.
function freeIds(out, boxId, count) {
  const ids = [];
  for (let n = 1; ids.length < count; n++) {
    const id = `${boxId}_snd${n}`;
    if (!out.boxesById[id] && !out.pluginToolbarsById[id]) ids.push(id);
  }
  return ids;
}

// Turns each sound-list AudioCue (deprecated) into single-file AudioCues on the
// timeline: the box keeps the first sound, every other sound gets a copy of the
// box, each keeping its autoplay / manual setting. The list is emptied, so a
// second run changes nothing. Lists the old player could not play unambiguously
// (two sounds starting together) stay on the old path.
export function migrateLegacySoundLists(snapshot) {
  const out = {
    boxesById: { ...(snapshot.boxesById || {}) },
    pluginToolbarsById: { ...(snapshot.pluginToolbarsById || {}) },
    navItemsById: { ...(snapshot.navItemsById || {}) },
    containedViewsById: { ...(snapshot.containedViewsById || {}) },
  };
  const anims = snapshot.animationsById || {};
  if (!Object.values(out.pluginToolbarsById).some(isCandidate)) return snapshot;

  Object.keys(out.pluginToolbarsById).forEach((boxId) => {
    const tb = out.pluginToolbarsById[boxId];
    const box = out.boxesById[boxId];
    if (!isCandidate(tb) || !box) return;
    const holder = holderOf(out, box, boxId);
    if (!holder) return;
    const sounds = tb.state.soundList
      .filter((s) => s && s.file)
      .sort((a, b) => startSecOf(a) - startSecOf(b));
    const starts = sounds.map(startSecOf);
    if (starts.some((t, i) => i > 0 && t === starts[i - 1])) return;

    // Read before the pieces get a timeline: after that every cue looks scene-started.
    const manual = audioCueManual(tb.state, box);
    const { startFrame } = resolveBoxTiming(box, anims[boxId] || []);
    const baseFrame = typeof startFrame === "number" ? startFrame : 0;
    const extra = freeIds(out, boxId, Math.max(0, sounds.length - 1));
    const ids = sounds.length ? [boxId, ...extra] : [];

    sounds.forEach((sound, i) => {
      const id = ids[i];
      const nextStartSec = i + 1 < sounds.length ? starts[i + 1] : null;
      out.boxesById[id] = {
        ...box,
        id,
        timeline: soundTimeline(box, baseFrame, sound, nextStartSec),
      };
      const volumeLevel = Number(sound.volumeLevel);
      out.pluginToolbarsById[id] = {
        ...tb,
        id,
        state: {
          ...tb.state,
          url: sound.file,
          manual,
          volume: Number.isFinite(volumeLevel) ? Math.round(volumeLevel * 100) : 100,
          soundList: [],
        },
      };
    });
    if (extra.length) {
      const at = holder.list.indexOf(boxId);
      holder.set([...holder.list.slice(0, at + 1), ...extra, ...holder.list.slice(at + 1)]);
    }
    // A list with no playable sound: only the deprecated list goes.
    if (!sounds.length)
      out.pluginToolbarsById[boxId] = { ...tb, state: { ...tb.state, soundList: [] } };
  });
  // Untouched slices keep their identity.
  Object.keys(out).forEach((key) => {
    const before = snapshot[key] || {};
    const same =
      Object.keys(out[key]).length === Object.keys(before).length &&
      Object.keys(out[key]).every((id) => out[key][id] === before[id]);
    if (same && snapshot[key]) out[key] = snapshot[key];
  });
  return Object.keys(out).every((key) => out[key] === snapshot[key]) ? snapshot : out;
}
